import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw, Target, Frown, Heart, Home, Clock, Flame } from "lucide-react";

export function LoseModal({ 
  isOpen, 
  score, 
  fruitsCollected, 
  targetCount,
  targetEmoji,
  hp,
  maxHP,
  canPlayAgain,
  onPlayAgain, 
  onBackToMap,
  movesLeft = 0,
  totalMoves = 0
}) {
  const progressPercent = Math.round((fruitsCollected / targetCount) * 100);
  const hpPercentage = Math.round((hp / maxHP) * 100);
  const isAlmostThere = progressPercent >= 80;
  const isSoClose = progressPercent >= 95;
  const movesShort = totalMoves > 0 ? Math.max(0, Math.ceil((targetCount - fruitsCollected) * (totalMoves / Math.max(fruitsCollected, 1)) - movesLeft)) : 0;

  const getNudgeMessage = () => {
    if (isSoClose) return { text: "SO CLOSE! Just a tiny bit more!", icon: "🔥", color: "text-orange-400" };
    if (progressPercent >= 90) return { text: "Almost had it! One more try!", icon: "💪", color: "text-yellow-400" };
    if (isAlmostThere) return { text: "Great run! You're nearly there!", icon: "⭐", color: "text-amber-400" };
    if (progressPercent >= 60) return { text: "Solid effort! Try chaining combos!", icon: "💡", color: "text-blue-400" };
    return null;
  };
  const nudge = getNudgeMessage();

  return (
    <Dialog open={isOpen}>
      <DialogContent className="sm:max-w-md bg-card border-2 border-destructive/30" data-testid="lose-modal">
        <DialogHeader className="text-center">
          {/* Icon changes based on how close the player was */}
          <div className="flex justify-center mb-4">
            {isAlmostThere ? (
              <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center animate-bounce-in">
                <Flame className="w-10 h-10 text-orange-400" />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center animate-bounce-in">
                <Frown className="w-10 h-10 text-destructive" />
              </div>
            )}
          </div>

          <DialogTitle className={`font-display text-3xl sm:text-4xl font-bold ${isAlmostThere ? 'text-orange-400' : 'text-destructive'}`}>
            {isSoClose ? 'So Close!' : isAlmostThere ? 'Almost!' : 'Out of Moves!'}
          </DialogTitle>
          
          <DialogDescription className="font-body text-base text-muted-foreground mt-2">
            You completed {progressPercent}% of the goal!
          </DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="space-y-3 py-4">
          <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
            <span className="font-body text-muted-foreground">Final Score</span>
            <span className="font-display font-bold text-foreground">{score}</span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <span className="font-body text-muted-foreground">{targetEmoji} Collected</span>
            </div>
            <span className="font-display font-bold text-primary">
              {fruitsCollected}/{targetCount}
            </span>
          </div>

          {/* HP remaining */}
          <div className="flex justify-between items-center p-3 bg-destructive/10 rounded-lg border border-destructive/20">
            <div className="flex items-center gap-2">
              <Heart className={`w-4 h-4 ${hpPercentage > 60 ? 'text-green-500' : hpPercentage > 30 ? 'text-yellow-500' : 'text-destructive'} fill-current`} />
              <span className="font-body text-muted-foreground">HP Remaining</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-20 h-3 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    hpPercentage > 60 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 
                    hpPercentage > 30 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' : 
                    'bg-gradient-to-r from-red-500 to-rose-400'
                  }`}
                  style={{ width: `${hpPercentage}%` }}
                />
              </div>
              <span className={`font-bold text-sm ${hpPercentage > 60 ? 'text-green-600' : hpPercentage > 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                {hp}/{maxHP}
              </span>
            </div>
          </div>

          {/* "Almost!" nudge with visual progress */}
          {nudge && (
            <div className="relative p-4 rounded-lg bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-orange-500/10 border border-orange-500/20 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent" style={{ width: `${progressPercent}%` }} />
              <div className="relative flex items-center justify-center gap-2">
                <span className="text-xl">{nudge.icon}</span>
                <p className={`font-display font-bold ${nudge.color}`}>{nudge.text}</p>
              </div>
              {/* Visual goal bar */}
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-700"
                  style={{ width: `${Math.min(progressPercent, 100)}%` }}
                />
              </div>
              <p className="text-center text-xs text-muted-foreground mt-1">{progressPercent}% of goal reached</p>
            </div>
          )}
          
          {/* Encouragement or warning */}
          {!nudge && (
            <div className={`p-4 rounded-lg text-center ${canPlayAgain ? 'bg-secondary' : 'bg-destructive/10'}`}>
              {canPlayAgain ? (
                <p className="font-body text-secondary-foreground">
                  Tip: Look for matches that create chain reactions!
                </p>
              ) : (
                <div className="flex items-center justify-center gap-2 text-destructive">
                  <Clock className="w-4 h-4" />
                  <p className="font-body">Not enough HP! Wait for HP to regenerate.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          {canPlayAgain ? (
            <Button 
              onClick={onPlayAgain}
              size="lg"
              className="w-full font-display font-semibold text-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-button"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Try Again
            </Button>
          ) : (
            <Button 
              onClick={onBackToMap}
              size="lg"
              className="w-full font-display font-semibold text-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-button"
            >
              <Home className="w-5 h-5 mr-2" />
              Back to Level Map
            </Button>
          )}
          
          {canPlayAgain && (
            <Button 
              onClick={onBackToMap}
              variant="outline"
              size="lg"
              className="w-full font-display font-semibold bg-card"
            >
              <Home className="w-5 h-5 mr-2" />
              Level Map
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
